/**
 * Validates the given password based on common password rules.
 *
 * - Password must be at least 8 characters long.
 * - Password must contain at least one uppercase letter.
 * - Password must contain at least one number.
 * - Password must contain at least one special character.
 *
 * @param {string} password - The password to be validated.
 * @returns {object} - An object containing validation results for each rule.
 */
export const validatePassword = (password) => {
  const validations = {
    IS_LONG_ENOUGH: false,
    HAS_UPPERCASE: false,
    HAS_NUMBER: false,
    HAS_SPECIAL_CHARACTER: false,
  };

  validations.IS_LONG_ENOUGH = password.length >= 8;
  validations.HAS_UPPERCASE = /[A-Z]/.test(password);
  validations.HAS_NUMBER = /[0-9]/.test(password);
  validations.HAS_SPECIAL_CHARACTER = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  return validations;
};

/**
 * Checks if the password and its confirmation match.
 *
 * @param {string} password - The original password.
 * @param {string} confirmPassword - The password confirmation input.
 * @returns {boolean} - True if the passwords match, false otherwise.
 */
export const doPasswordsMatch = (password, confirmPassword) =>
  !!password && !!confirmPassword && password === confirmPassword;

/**
 * Calculates the strength of the password based on various criteria:
 *
 * - Length of password (at least 8 characters).
 * - Contains at least one uppercase letter.
 * - Contains at least one lowercase letter.
 * - Contains at least one number.
 * - Contains at least one special character.
 *
 * Returns a string indicating the strength of the password: 'Weak', 'Moderate', or 'Strong'.
 *
 * @param {string} password - The password to be evaluated.
 * @returns {string} - A string indicating the password strength.
 */
export const calculatePasswordStrengh = (password) => {
  let strength = 0;
  if (password.length >= 8) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[a-z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[!@#*]/.test(password)) strength++;

  if (strength <= 2) return 'Weak';
  if (strength === 3) return 'Moderate';
  return 'Strong';
};
